import { openDB, type IDBPDatabase } from 'idb'
import type { SaveData, GameSettings } from '../types'

const DB_NAME = 'zettaranc-game'
const DB_VERSION = 1
const STORE_NAME = 'saveData'
const SAVE_KEY = 'currentSave'

const DEFAULT_SETTINGS: GameSettings = {
  soundEnabled: true,
  animationSpeed: 'normal',
  fontSize: 'medium',
}

const DEFAULT_SAVE: SaveData = {
  progress: {},
  statistics: {
    totalCards: 0,
    completedCards: 0,
    totalAttempts: 0,
    correctRate: 0,
    weaknessAreas: [],
    studyTimeMs: 0,
  },
  settings: DEFAULT_SETTINGS,
}

/**
 * 初始化数据库
 */
async function initDB(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    },
  })
}

/**
 * 保存游戏数据
 */
export async function saveGame(data: SaveData): Promise<void> {
  const db = await initDB()
  await db.put(STORE_NAME, data, SAVE_KEY)
}

/**
 * 加载游戏数据
 */
export async function loadGame(): Promise<SaveData> {
  const db = await initDB()
  const data = await db.get(STORE_NAME, SAVE_KEY)
  return data ?? DEFAULT_SAVE
}

/**
 * 更新设置
 */
export async function updateSettings(settings: Partial<GameSettings>): Promise<void> {
  const current = await loadGame()
  const updated: SaveData = {
    ...current,
    settings: { ...current.settings, ...settings },
  }
  await saveGame(updated)
}

/**
 * 更新上次学习位置
 */
export async function updateLastStudyLocation(
  chapterId: number,
  sectionId: string
): Promise<void> {
  const current = await loadGame()
  const updated: SaveData = {
    ...current,
    lastStudyLocation: { chapterId, sectionId },
  }
  await saveGame(updated)
}

/**
 * 重置所有数据
 */
export async function resetGame(): Promise<void> {
  await saveGame(DEFAULT_SAVE)
}
